// #region Règles d’affichage
/** Texte comparé sans les différences d'espaces (retours à la ligne du gabarit, espaces insécables). */
function normaliser(texte: string): string {
  return texte.replace(/\s+/g, ' ').trim();
}

/**
 * Règle de Claire : pas d'infobulle qui répète un texte qu'on lit déjà en entier. Vrai quand l'infobulle reprend
 * exactement le texte affiché par l'élément et qu'aucune partie de ce texte n'est coupée (ni l'élément ni un
 * élément intérieur ne déborde, ex. « … » de .tronque) ; une infobulle qui apporte autre chose reste affichée.
 * Texte affiché : innerText, sans le texte masqué (ex. libellés du menu réduit, où seule l'icône se voit).
 */
export function texteDejaVisible(el: HTMLElement, texte: string): boolean {
  // innerText absent (jsdom des tests, qui ne calcule pas l'affichage) : tout le texte.
  const affiche = typeof el.innerText === 'string' ? el.innerText : el.textContent;
  if (normaliser(affiche ?? '') !== normaliser(texte)) {
    return false;
  }
  const coupe = (e: Element) => e.scrollWidth > e.clientWidth + 1 || e.scrollHeight > e.clientHeight + 1;
  return !coupe(el) && ![...el.querySelectorAll('*')].some((e) => e.clientWidth > 0 && coupe(e));
}

/**
 * Position de l'infobulle (coin haut gauche, px) pour un élément de rectangle `r`, dans une fenêtre `vw` × `vh` :
 * - « dessous » (par défaut) : centrée sous l'élément, au-dessus s'il n'y a pas la place (haut = vrai) ;
 * - « droite » (attribut data-gt-cote="droite", ex. chevron du menu) : à droite, centrée en hauteur ; dessous si elle
 *   sortirait de la fenêtre.
 * L'infobulle reste à 8 px des bords de la fenêtre.
 */
export function positionInfobulle(
  r: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom' | 'width' | 'height'>,
  w: number,
  h: number,
  vw: number,
  vh: number,
  cote: 'dessous' | 'droite' = 'dessous',
): { x: number; y: number; haut: boolean } {
  if (cote === 'droite' && r.right + 8 + w <= vw - 8) {
    return { x: r.right + 8, y: Math.max(8, Math.min(r.top + r.height / 2 - h / 2, vh - h - 8)), haut: false };
  }
  const x = Math.max(8, Math.min(r.left + r.width / 2 - w / 2, vw - w - 8));
  const dessous = r.bottom + 8;
  return dessous + h > vh - 8 ? { x, y: r.top - h - 8, haut: true } : { x, y: dessous, haut: false };
}

/**
 * Déplace le title de l'élément dans data-gt (supprime l'infobulle native du navigateur). Le title servait aussi de nom
 * accessible (boutons icône) : il est gardé en aria-label pour les lecteurs d'écran (data-gt-aria : aria-label posé
 * ici, donc tenu à jour quand le title change). Faux si le title est vide (plus d'infobulle, data-gt retiré).
 */
function deplacerTitle(el: HTMLElement): boolean {
  const t = el.getAttribute('title');
  el.removeAttribute('title');
  if (!t) {
    el.removeAttribute('data-gt');
    return false;
  }
  el.setAttribute('data-gt', t);
  if (el.hasAttribute('data-gt-aria') || (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby'))) {
    el.setAttribute('aria-label', t);
    el.setAttribute('data-gt-aria', '');
  }
  return true;
}
// #endregion

// #region Installation des écouteurs
/**
 * Infobulles de la maquette : tout attribut `title` de l'application est affiché dans une
 * infobulle au style Logaia (#gtip, styles/logaia-v3.css) au lieu de l'infobulle du navigateur.
 * Une infobulle qui ne ferait que répéter un texte entièrement visible n'est pas affichée (texteDejaVisible).
 * Reprise du script global de « Refonte Logaia - VERSION 3.html ». À appeler une fois au démarrage.
 */
export function installerInfobulles(doc: Document = document): void {
  const tip = doc.createElement('div');
  tip.id = 'gtip';
  tip.setAttribute('role', 'tooltip');
  doc.body.appendChild(tip);
  let courant: HTMLElement | null = null;
  /** Élément cliqué : plus d'infobulle tant que la souris ne l'a pas quitté (sinon elle revient sur le menu ouvert). */
  let clique: HTMLElement | null = null;

  const cacher = () => {
    tip.classList.remove('on');
    courant = null;
  };

  doc.addEventListener('mouseover', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[title],[data-gt]');
    if (!el || el === clique || el.tagName === 'IFRAME' || el.closest('#gtip')) {
      return;
    }
    if (el.hasAttribute('title') && !deplacerTitle(el)) {
      // Title vidé par Angular (ex. menu déplié) : plus d'infobulle.
      if (el === courant) {
        cacher();
      }
      return;
    }
    const texte = el.getAttribute('data-gt');
    if (!texte || texteDejaVisible(el, texte)) {
      return;
    }
    courant = el;
    tip.textContent = texte;
    tip.classList.add('on');
    const cote = el.getAttribute('data-gt-cote') === 'droite' ? 'droite' : 'dessous';
    const p = positionInfobulle(el.getBoundingClientRect(), tip.offsetWidth, tip.offsetHeight, innerWidth, innerHeight, cote);
    tip.style.left = `${p.x}px`;
    tip.style.top = `${p.y}px`;
    tip.dataset['up'] = p.haut ? '1' : '';
  });

  doc.addEventListener('mouseout', (e) => {
    if (courant && !courant.contains(e.relatedTarget as Node)) {
      cacher();
    }
    if (clique && !clique.contains(e.relatedTarget as Node)) {
      clique = null;
    }
  });
  doc.addEventListener('mousedown', (e) => {
    clique = (e.target as HTMLElement | null)?.closest?.<HTMLElement>('[title],[data-gt]') ?? null;
    cacher();
  });
  addEventListener('scroll', cacher, true);
  // Élément survolé retiré de la page (ligne supprimée, menu refermé…) : aucun mouseout n'arrive, on cache ici.
  new MutationObserver(() => {
    if (courant && !courant.isConnected) {
      cacher();
    }
  }).observe(doc.body, { childList: true, subtree: true });
  // Title modifié par l'application sur un élément déjà survolé (ex. chevron « Replier » → « Déplier », menu replié
  // puis déplié : title vidé) : le nouveau texte remplace l'ancien dans data-gt, et le title est retiré tout de suite
  // (sinon le navigateur affiche son infobulle native, la souris étant déjà sur l'élément) ; l'infobulle en cours se
  // ferme. Sans attribut title, le texte a déjà été déplacé (rien à faire).
  new MutationObserver((mutations) => {
    for (const el of new Set(mutations.map((m) => m.target as HTMLElement))) {
      if (!el.hasAttribute('title') || !el.hasAttribute('data-gt')) {
        continue;
      }
      deplacerTitle(el);
      if (el === courant) {
        cacher();
      }
    }
  }).observe(doc.body, { attributes: true, attributeFilter: ['title'], subtree: true });

  // Élément indisponible (.is-off) avec infobulle : il garde le survol pour l'infobulle
  // (styles.css), mais ni le clic ni Entrée / Espace ne l'activent.
  doc.addEventListener(
    'click',
    (e) => {
      if ((e.target as Element | null)?.closest?.('.is-off')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true,
  );
}
// #endregion
